import httpClient from '@/api/httpClient'
import { defineStore } from 'pinia'
import store from '@/store'

export const useTransformStore = defineStore('transform', {
  state: () => ({
    transform: {
      id: null,
      name: '',
      transform_rules: []
    }
  }),

  getters: {
    hasNewRecord: ({ transform: { transform_rules } }) => {
      return transform_rules.some(({ id }) => !id)
    }
  },

  actions: {
    async fetch({ transformId }) {
      return httpClient
        .get(`/api/internal/transforms/${transformId}`)
        .then(response => {
          this.transform = response.data.transform
        })
        .catch(err => {
          console.error(err)
          store.dispatch('messages/addError', err)
        })
    },

    async create() {
      const { transform } = this

      return httpClient
        .post('/api/internal/transforms/', { transform })
        .then(({ data }) => {
          this.transform = data.transform
          store.dispatch('messages/addNotice', 'Document transform created')
        })
        .catch(err => {
          console.error(err)
          store.dispatch('messages/addError', err)
          throw err
        })
    },

    async update() {
      const { transform } = this

      return httpClient
        .patch(`/api/internal/transforms/${transform.id}`, { transform })
        .then(({ data }) => {
          this.transform = data.transform
          store.dispatch('messages/addNotice', 'Document transform saved')
        })
        .catch(err => {
          console.error(err)
          store.dispatch('messages/addError', err)
          throw err
        })
    },

    addRule(type) {
      const { id, transform_rules } = this.transform

      // Only allow one new record at a time
      if (transform_rules.some(rule => !rule.id)) return

      this.transform.transform_rules.push({
        id: null,
        transform_id: id,
        type,
        original: '',
        replacement_start: '',
        replacement_end: ''
      })
    },

    async saveRule({ transform_rule }) {
      const action = transform_rule.id ? 'updateRule' : 'createRule'

      return this[action]({ transform_rule })
    },

    async createRule({ transform_rule }) {
      return httpClient.post('/api/internal/transform_rules', { transform_rule })
        .then(({ data: { transform_rule, message } }) => {
          this.transform.transform_rules.push(transform_rule)
          this.transform.transform_rules = this.transform.transform_rules.filter(({ id }) => id)

          store.dispatch('messages/addNotice', message)
        })
        .catch(err => {
          console.error(err)
          store.dispatch('messages/smartAdd', err)
        })
    },

    async updateRule({ transform_rule }) {
      const { id } = transform_rule

      return httpClient.patch(`/api/internal/transform_rules/${id}`, { transform_rule })
        .then(({ data: { transform_rule, message } }) => {
          this.transform.transform_rules = this.transform.transform_rules.map(rule =>
            rule.id === transform_rule.id ? transform_rule : rule
          )

          store.dispatch('messages/addNotice', message)
        })
        .catch(err => {
          console.error(err)
          store.dispatch('messages/smartAdd', err)
        })
    },

    async destroyRule({ id }) {
      if (id) {
        return httpClient.delete(`/api/internal/transform_rules/${id}`)
          .then(() => {
            this.transform.transform_rules = this.transform.transform_rules.filter(rule => rule.id !== id)
          })
          .catch(err => {
            console.error(err)
            store.dispatch('messages/smartAdd', err)
          })
      } else {
        this.transform.transform_rules = this.transform.transform_rules.filter(({ id }) => id)
      }
    }
  }
})
